import { ref } from "vue";
import { defineStore } from "pinia";
import type { CartItem } from "~/types/general";

export const useCartStore = defineStore("CartStore", () => {
   // state
   const cart = ref<CartItem[]>([]);

   // getters
   const getSubTotalById = computed(() => {
      return (product_id: number) => {
         const product = cart.value.find(
            (item) => item.product_id === product_id,
         );
         if (product) {
            const addOnsPrice = product.add_ons ? product.add_ons.price : 0;
            const pickupTypePrice = product.pickup_type
               ? product.pickup_type.price
               : 0;
            const additionalTourPrice = product.additional_tour
               ? product.additional_tour.price
               : 0;
            const subTotal = product.participants.reduce((acc, option) => {
               return (
                  acc +
                  (option.price +
                     pickupTypePrice +
                     additionalTourPrice +
                     addOnsPrice) *
                     option.count
               );
            }, 0);
            return Number(subTotal.toFixed(2));
         }
         return 0;
      };
   });

   const getTotalCart = computed(() => {
      return cart.value.reduce((acc, item) => {
         const addOnsPrice = item.add_ons ? item.add_ons.price : 0;
         const pickupTypePrice = item.pickup_type ? item.pickup_type.price : 0;
         const additionalTourPrice = item.additional_tour
            ? item.additional_tour.price
            : 0;
         const subTotal = item.participants.reduce((acc, option) => {
            return (
               acc +
               (option.price +
                  pickupTypePrice +
                  additionalTourPrice +
                  addOnsPrice) *
                  option.count
            );
         }, 0);

         const total = acc + subTotal;
         return Number(total.toFixed(2));
      }, 0);
   });

   const getItinerary = computed(() => {
      return (product_id: number) => {
         const product = cart.value.find(
            (item) => item.product_id === product_id,
         );
         return product ? product.itinerary : [];
      };
   });

   //actions
   function addProduct(product: CartItem) {
      // check if this product is already in the cart
      const existingProduct = cart.value.find(
         (item) => item.product_id === product.product_id,
      );
      // if it is, todo enable same product id
      if (existingProduct) {
         return;
      }

      cart.value.push(product);
   }

   function getOptionsWithCount(id: number) {
      return cart.value.find((item) => item.product_id === id)?.participants;
   }

   function increment(product_id: number, index: number) {
      const product = cart.value.find((item) => item.product_id === product_id);

      if (product) {
         const option = product.participants[index];

         // Increment the count of the selected option, add initial first add is min
         if (option.count === 0) {
            option.count = option.min; // Set to minimum value on first add
         } else if (option.count < option.max) {
            option.count++;
         }
      }
   }

   function decrement(product_id: number, index: number) {
      const product = cart.value.find((item) => item.product_id === product_id);

      if (product) {
         const option = product.participants[index];

         // Decrement the count of the selected option
         if (option.count > option.min) {
            option.count--;
         } else {
            option.count = 0;
         }
      }
   }

   function addOptions(
      product_id: number,
      keyName: keyof CartItem,
      payload: any,
   ) {
      const productIndex = cart.value.findIndex(
         (item) => item.product_id === product_id,
      );
      if (productIndex !== -1) {
         const product = cart.value[productIndex];
         const updatedProduct = {
            ...product,
            [keyName]: payload,
         };
         cart.value[productIndex] = updatedProduct;
      }
   }

   function insertItinerary(product_id: number, payload: any) {
      const productIndex = cart.value.findIndex(
         (item) => item.product_id === product_id,
      );

      if (productIndex === -1) return;

      const itinerary = cart.value[productIndex].itinerary;
      const existingItinerary = itinerary.find(
         (item) => item.type === payload.type,
      );

      if (payload.name === "None") {
         if (existingItinerary) {
            removeItinerary(itinerary, existingItinerary);
         }
         return;
      }

      if (existingItinerary) {
         replaceItinerary(itinerary, existingItinerary, payload);
      } else {
         addItinerary(itinerary, payload);
      }
   }

   function removeItinerary(itinerary: any[], existingItinerary: any) {
      const index = itinerary.indexOf(existingItinerary);
      itinerary.splice(index, 1);
   }

   function replaceItinerary(
      itinerary: any[],
      existingItinerary: any,
      payload: any,
   ) {
      const index = itinerary.indexOf(existingItinerary);
      itinerary[index] = payload;
   }

   function addItinerary(itinerary: any[], payload: any) {
      itinerary.push(payload);
   }

   return {
      cart,
      addProduct,
      getOptionsWithCount,
      increment,
      decrement,
      addOptions,
      getSubTotalById,
      getTotalCart,
      insertItinerary,
      getItinerary,
   };
});

if ((import.meta as any).hot) {
   (import.meta as any).hot.accept(
      acceptHMRUpdate(useCartStore, (import.meta as any).hot),
   );
}
